import {
  appliDuPortail,
  categorieDepotExigee,
  depotPermisParCategorie,
  formaterJour,
} from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { api, ecrit, ErreurApi, lire, requeteMoi, requetes } from '../api'
import { BilanEcritures } from '../composants/BilanEcritures'
import { type BilanConnexion, requeteBilan } from '../composants/BilanJustificatifs'
import {
  aideValidation,
  confirmationCouperEcriture,
  confirmationOuvrirEcriture,
  confirmationValidation,
  conseilDistinct,
  gestesEcriture,
  instantCourt,
  jourCourt,
  LIBELLES_MODE_PASSAGE,
  LIBELLES_STATUT_PASSAGE,
  messageRapprochement,
  relancePossible,
  resumeComptes,
  resumeEcritures,
  suresAEcrire,
  texteCategorie,
  texteDeclencheur,
  texteEtatEcriture,
  texteRelecture,
} from '../composants/bilan-justificatifs'
import { formaterInstant } from '../composants/Historique'
import { Icone } from '../composants/Icone'
import { Vide } from '../composants/Section'
import { confirmer } from '../composants/confirmation'

// Bilan des justificatifs d'une connexion de portail du cabinet (Tiime, Dougs), ouvert depuis
// Comptabilité, Justificatifs : en haut, le fil vers la liste des connexions, « Ouvrir Tiime » et
// « Rapprocher maintenant » (comme dans Réglages, Portails) ; puis le dernier passage en une ligne
// (quand, quelle période, à blanc ou écriture ouverte, « Valider ce passage à blanc » pour le
// titulaire), ses détails repliés (mode et motif, lecture, dossier reconnu par le SIREN,
// catégorie, relecture, écriture et ce qu'elle a fait, validation), l'écriture de la connexion
// (le titulaire l'ouvre ; le titulaire ou un administrateur la coupe) ; enfin les écritures du
// cabinet et leurs pièces (`BilanEcritures`), où toute personne qui écrit sur l'entité décide
// (« C'est celle-ci » compris) et lance les recherches. Aucun bouton n'écrit chez le portail :
// seul l'agent du Mac y dépose puis relie les sûres, choisies comprises, écriture ouverte.

export function Justificatifs({ id }: { id: string }) {
  const moi = useQuery(requeteMoi).data
  const bilan = useQuery(requeteBilan(id))

  if (bilan.isPending)
    return (
      <main>
        <Vide>Chargement…</Vide>
      </main>
    )
  if (!bilan.data)
    return (
      <main>
        <p className="alerte">Connexion introuvable.</p>
        <Link to="/justificatifs">Retour aux justificatifs</Link>
      </main>
    )
  const b = bilan.data
  const entiteId = b.connexion.entiteId
  const ecriture = moi !== undefined && entiteId !== null && ecrit(moi, entiteId)
  const appli = appliDuPortail(b.connexion.fournisseur)

  return (
    <main className="page-justificatifs">
      <div className="bilan-fil">
        <Link to="/justificatifs" className="bilan-fil-retour">
          <Icone nom="arrow_back" taille={18} />
          Justificatifs
        </Link>
        <span className="bilan-fil-separateur">/</span>
        <span className="discret">
          {[b.connexion.portail, b.connexion.entite].filter(Boolean).join(' · ')}
        </span>
        <div className="bilan-fil-actions">
          {appli && (
            <a className="bouton-discret" href={appli} target="_blank" rel="noreferrer">
              <Icone nom="open_in_new" />
              Ouvrir {b.connexion.portail}
            </a>
          )}
          {ecriture && moi && <Relancer b={b} moi={moi.id} />}
        </div>
      </div>
      <header className="entete-fiche">
        <h1>
          Justificatifs : {b.connexion.libelle}
          {!b.connexion.active && <span className="badge">Désactivée</span>}
        </h1>
        <p className="discret">
          {[
            b.connexion.entite,
            b.connexion.portail,
            b.connexion.titulaire ? `titulaire ${b.connexion.titulaire}` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </header>
      <EntetePassage b={b} />
      <BilanEcritures b={b} ecriture={ecriture} />
    </main>
  )
}

/**
 * Le dernier passage en une ligne : quand, quelle période, à blanc ou écriture ouverte, « Valider
 * ce passage à blanc » pour le titulaire ; les alertes (erreur, conseil, dépôts à vérifier) ; ses
 * détails dans un dépliant fermé par défaut ; puis l'écriture de la connexion (ouvrir, couper).
 */
function EntetePassage({ b }: { b: BilanConnexion }) {
  const client = useQueryClient()
  const [occupe, setOccupe] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [details, setDetails] = useState(false)
  const p = b.passage
  const portail = b.connexion.portail
  const possible = b.validation.possible
  const ecritureOuverte = b.ecriture.active
  const categorie = p
    ? texteCategorie(p.categorie, { exigee: categorieDepotExigee(b.connexion.fournisseur) })
    : null
  const ecritures = p ? resumeEcritures(p.ecritures) : []

  async function valider(collecteId: string) {
    if (
      !(await confirmer(
        confirmationValidation({ libelle: b.connexion.libelle, portail, ecritureOuverte }),
      ))
    )
      return
    setErreur(null)
    setMessage(null)
    setOccupe(true)
    try {
      await lire(
        api.portails[':id']['valider-passage'].$post({
          param: { id: b.connexion.id },
          json: { collecteId },
        }),
      )
      setMessage('Passage à blanc validé.')
      await client.invalidateQueries()
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Validation impossible.')
    } finally {
      setOccupe(false)
    }
  }

  const resume = p
    ? resumeComptes(
        {
          lues: p.lecture?.lues ?? null,
          etats: p.bilan?.etats ?? null,
          marquees: p.bilan?.marquees,
        },
        {
          ecritureOuverte: b.ecriture.active,
          depotPermis: depotPermisParCategorie({
            exigee: categorieDepotExigee(b.connexion.fournisseur),
            categorie: p.categorie,
          }),
        },
      )
    : []

  return (
    <section className="carte entete-passage">
      <div className="passage-ligne">
        {!p ? (
          <span className="discret">
            Aucun passage encore : « Rapprocher maintenant » le lance.
          </span>
        ) : (
          <>
            <span className="passage-fait">
              <Icone nom="history" />
              <span className="discret">Dernier passage</span>
              <strong>{instantCourt(p.creeLe, new Date())}</strong>
              {p.statut !== 'terminee' && (
                <span className="discret">({LIBELLES_STATUT_PASSAGE[p.statut]})</span>
              )}
            </span>
            {p.fenetre && (
              <span className="passage-fait">
                <Icone nom="date_range" />
                <span className="discret">Écritures du</span>
                <strong>
                  {jourCourt(p.fenetre.decisionDu)} au {jourCourt(p.fenetre.au, true)}
                </strong>
              </span>
            )}
          </>
        )}
        <span className={`badge grand ${ecritureOuverte ? 'ton-ok' : 'ton-ambre'}`}>
          <Icone nom={ecritureOuverte ? 'edit_note' : 'visibility'} />
          {ecritureOuverte ? 'Écriture ouverte' : `À blanc : rien n’est écrit dans ${portail}`}
        </span>
        <div className="passage-actions">
          {p && (
            <button
              type="button"
              className="bouton-discret bouton-details"
              aria-expanded={details}
              onClick={() => setDetails((x) => !x)}
            >
              Détails du passage
              <Icone nom={details ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} />
            </button>
          )}
          {possible && (
            <button
              type="button"
              className="bouton"
              disabled={occupe}
              title={aideValidation({ portail, ecritureOuverte })}
              onClick={() => void valider(possible.collecteId)}
            >
              <Icone nom="task_alt" />
              Valider ce passage à blanc
            </button>
          )}
        </div>
      </div>
      {possible && b.passageCalcule && b.passageCalcule.collecteId !== possible.collecteId && (
        <p className="alerte">
          Les listes ne viennent pas de ce passage : recharger la page avant de valider.
        </p>
      )}
      {possible && b.documentsSansLiaison === null && (
        <p className="alerte">
          Les justificatifs déjà dans {portail} sans liaison n’ont pas été lus : vérifier dans{' '}
          {portail} qu’aucun ne ferait doublon avant de valider.
        </p>
      )}
      {p?.erreur && <p className="alerte">{p.erreur}</p>}
      {p && conseilDistinct(p.conseil, p.erreur) && (
        <p className="alerte" role="status">
          {p.conseil}
        </p>
      )}
      {b.aVerifier.length > 0 && (
        <p className="alerte" role="status">
          {b.aVerifier.length === 1
            ? `Un dépôt de l’agent est à vérifier dans ${portail}`
            : `${b.aVerifier.length} dépôts de l’agent sont à vérifier dans ${portail}`}{' '}
          : liste « À vérifier », en bas de la page.
        </p>
      )}
      {p && details && (
        <div className="passage-details">
          {/* Les nombres de la fin du passage ; les onglets ci-dessous suivent les décisions. */}
          {resume.length > 0 && <p>À la fin du passage : {resume.join(' · ')}.</p>}
          {possible && <p className="discret">{aideValidation({ portail, ecritureOuverte })}</p>}
          <dl className="details">
            <dt>Passage</dt>
            <dd>
              {formaterInstant(p.creeLe)} · {texteDeclencheur(p)} ·{' '}
              {LIBELLES_STATUT_PASSAGE[p.statut]}
              {p.finLe && p.statut === 'terminee' && ` le ${formaterInstant(p.finLe)}`}
            </dd>
            <dt>Mode</dt>
            <dd>
              {p.mode && <span className="badge">{LIBELLES_MODE_PASSAGE[p.mode]}</span>}{' '}
              {p.mode === 'ecriture'
                ? `Écriture : les correspondances sûres sont déposées et reliées dans ${portail}.`
                : p.mode === 'a_blanc'
                  ? `À blanc : rien n’est écrit dans ${portail}.`
                  : 'Fixé quand l’ordinateur prend le passage.'}
              {p.libelleMode && <div className="discret">{p.libelleMode}.</div>}
            </dd>
            <dt>Période</dt>
            <dd>
              {p.fenetre
                ? `Opérations du ${formaterJour(p.fenetre.decisionDu)} au ${formaterJour(p.fenetre.au)}, lues depuis le ${formaterJour(p.fenetre.lectureDu)}.`
                : 'Fixée quand l’ordinateur prend le passage.'}
            </dd>
            <dt>Lecture</dt>
            <dd>
              {p.lecture ? (
                <>
                  {p.lecture.lues} opération{p.lecture.lues > 1 ? 's' : ''} lue
                  {p.lecture.lues > 1 ? 's' : ''} en {p.lecture.lots} lot
                  {p.lecture.lots > 1 ? 's' : ''}
                  {p.complete
                    ? ', complète.'
                    : ' : incomplète, aucune correspondance n’est sûre ni reconnue.'}
                  {p.lecture.illisibles > 0 && (
                    <div className="discret">
                      {p.lecture.illisibles} ligne{p.lecture.illisibles > 1 ? 's' : ''} illisible
                      {p.lecture.illisibles > 1 ? 's' : ''}.
                    </div>
                  )}
                </>
              ) : (
                'Pas encore lue.'
              )}
            </dd>
            <dt>Dossier</dt>
            <dd>
              {p.dossier
                ? 'Reconnu par le SIREN de l’entité.'
                : b.connexion.dossierRetenu
                  ? 'Retenu au premier passage, pas encore relu par celui-ci.'
                  : 'Pas encore reconnu.'}
            </dd>
            {p.lecture && (
              <>
                {categorie && (
                  <>
                    <dt>Catégorie</dt>
                    <dd>{categorie}</dd>
                  </>
                )}
                <dt>Relecture</dt>
                <dd>{texteRelecture(p.lecture.relecture)}</dd>
              </>
            )}
            <dt>Écriture</dt>
            <dd>
              {texteEtatEcriture(b.ecriture, {
                portail,
                ouverteLe: b.ecriture.ouverteLe ? formaterInstant(b.ecriture.ouverteLe) : null,
                coupeeLe: b.ecriture.coupeeLe ? formaterInstant(b.ecriture.coupeeLe) : null,
              }).map((ligne, i) => (
                <div key={ligne} className={i === 0 ? undefined : 'discret'}>
                  {ligne}
                </div>
              ))}
            </dd>
            {(ecritures.length > 0 || p.ecrituresArretees) && (
              <>
                <dt>Fait dans {portail}</dt>
                <dd>
                  {ecritures.length > 0 ? `${ecritures.join(' · ')}.` : 'Rien.'}
                  {p.ecrituresArretees && (
                    <div className="discret">
                      Écritures arrêtées en cours de passage : voir le conseil ci-dessus.
                    </div>
                  )}
                </dd>
              </>
            )}
            <dt>Validation</dt>
            <dd>
              {b.validation.valideLe
                ? `Passage à blanc validé le ${formaterInstant(b.validation.valideLe)}${b.validation.valideVersion !== null ? ` (version ${b.validation.valideVersion} de la règle)` : ''}${b.validation.enVigueur ? '.' : ', tombée depuis la coupure de l’écriture.'}`
                : 'Passage à blanc pas encore validé par le titulaire.'}
            </dd>
          </dl>
        </div>
      )}
      {message && (
        <p className="discret" role="status">
          {message}
        </p>
      )}
      {erreur && <p className="alerte">{erreur}</p>}
      <ReglageEcriture b={b} />
    </section>
  )
}

/**
 * « Ouvrir l'écriture » pour le titulaire, quand tous les verrous sont ouverts, sinon le motif en
 * clair ; « Couper l'écriture » pour le titulaire ou un administrateur, quand elle est ouverte.
 * Ce que le serveur rend au lecteur fait foi (`ecriture.possible`) ; chaque geste est confirmé,
 * journalisé, jamais via l'assistant. Ni l'un ni l'autre n'écrit chez le portail.
 */
function ReglageEcriture({ b }: { b: BilanConnexion }) {
  const client = useQueryClient()
  const [occupe, setOccupe] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const g = gestesEcriture(b.ecriture)
  const portail = b.connexion.portail
  const libelle = b.connexion.libelle
  // Les correspondances sûres que montre le bilan et que l'agent peut écrire (sans catégorie de
  // dépôt chez un portail qui l'exige, seules celles déjà déposées se relient).
  const sures = suresAEcrire(b.operations, { depotPermis: b.passageCalcule?.depotPermis ?? true })

  async function regler(ouvrir: boolean) {
    if (
      !(await confirmer(
        ouvrir
          ? confirmationOuvrirEcriture({ libelle, portail, sures })
          : confirmationCouperEcriture({ libelle, portail }),
      ))
    )
      return
    setErreur(null)
    setMessage(null)
    setOccupe(true)
    try {
      await lire(
        api.portails[':id'].ecriture.$post({ param: { id: b.connexion.id }, json: { ouvrir } }),
      )
      setMessage(ouvrir ? 'Écriture ouverte.' : 'Écriture coupée.')
      // L'état de la connexion, son bilan et ses tâches en dépendent.
      await client.invalidateQueries()
    } catch (e) {
      setErreur(
        e instanceof ErreurApi
          ? e.message
          : ouvrir
            ? 'Ouverture impossible.'
            : 'Coupure impossible.',
      )
    } finally {
      setOccupe(false)
    }
  }

  if (!g.ouvrir && !g.couper && !g.motif && !message && !erreur) return null
  // Rien à faire ici (pas de bouton) : une ligne discrète dit pourquoi l'écriture reste fermée.
  if (!g.ouvrir && !g.couper && !message && !erreur)
    return (
      <p className="discret passage-ecriture">
        <Icone nom="edit_note" taille={16} />
        <span>
          Écriture dans {portail} : {g.motif}
        </span>
      </p>
    )
  return (
    <div className="section-ajout">
      <strong>Écriture dans {portail}</strong>
      {g.ouvrir && (
        <p className="discret">
          L’agent du Mac déposera puis reliera les seules correspondances sûres, à vos passages et
          au passage mensuel ; rien d’autre n’est écrit dans {portail}. Faire le premier passage
          soi-même (« Rapprocher maintenant »), puis le vérifier dans {portail}.
        </p>
      )}
      {g.couper && (
        <p className="discret">
          Couper arrête toute écriture de l’agent dans {portail} ; il faudra un nouveau passage à
          blanc validé pour rouvrir.
        </p>
      )}
      {g.motif && <p className="discret">{g.motif}</p>}
      {(g.ouvrir || g.couper) && (
        <div className="boutons">
          {g.ouvrir && (
            <button
              type="button"
              className="bouton bouton-principal"
              disabled={occupe}
              onClick={() => void regler(true)}
            >
              Ouvrir l’écriture
            </button>
          )}
          {g.couper && (
            <button
              type="button"
              className="bouton-discret bouton-danger"
              disabled={occupe}
              onClick={() => void regler(false)}
            >
              <Icone nom="stop_circle" /> Couper l’écriture
            </button>
          )}
        </div>
      )}
      {message && (
        <p className="discret" role="status">
          {message}
        </p>
      )}
      {erreur && <p className="alerte">{erreur}</p>}
    </div>
  )
}

/**
 * « Rapprocher maintenant », comme dans Réglages, Portails : la même route (`POST
 * /portails/:id/collecter`), les mêmes conditions (`relancePossible` : connexion active, relevée,
 * rien qui empêche sa relève, aucune collecte en cours sauf celle qu'il reprend), le même message. Le serveur décide : un
 * passage demandé par une autre personne que le titulaire reste à blanc et attend son « C'est
 * parti ».
 */
function Relancer({ b, moi }: { b: BilanConnexion; moi: string }) {
  const client = useQueryClient()
  const connexion = useQuery(requetes.portails).data?.connexions.find(
    (c) => c.id === b.connexion.id,
  )
  const [occupe, setOccupe] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  if (!connexion || (!relancePossible(connexion) && !message && !erreur)) return null

  async function relancer() {
    if (!connexion) return
    setErreur(null)
    setMessage(null)
    setOccupe(true)
    try {
      await lire(api.portails[':id'].collecter.$post({ param: { id: connexion.id } }))
      setMessage(messageRapprochement(connexion, { moi, portail: b.connexion.portail }))
      // La connexion, son bilan et son historique en dépendent.
      await client.invalidateQueries({ queryKey: ['portails'] })
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Action impossible.')
    } finally {
      setOccupe(false)
    }
  }

  return (
    <div className="relancer">
      {relancePossible(connexion) && (
        <button
          type="button"
          className="bouton bouton-principal"
          disabled={occupe}
          onClick={() => void relancer()}
        >
          <Icone nom="sync" />
          Rapprocher maintenant
        </button>
      )}
      {message && (
        <p className="discret relancer-message" role="status">
          {message}
        </p>
      )}
      {erreur && <p className="alerte relancer-message">{erreur}</p>}
    </div>
  )
}
